// src/modules/units/tyres/shared/tyre-metrics.js
//
// Per-tyre derived numbers. Not aggregates — everything here answers a question
// about ONE tyre from fields already on its record, which is what makes these
// safe to use as table columns as well as report inputs.
//
// They live here rather than in the reports module because they now have two
// consumers: computeFleetReport()'s attention flags, and the column registry
// (tyre-columns.js), which offers them as columns and exports them to CSV. A
// shared module cannot import from a view, and the alternative — a second copy
// of the arithmetic — is the dual-source-of-truth this subsystem keeps deleting.
//
// Both return null, never 0 or '', when the inputs cannot answer the question.
// null is "we don't know"; 0 is a real measurement. Each consumer applies its own
// blank convention to null (the table shows an em dash, the CSV an empty cell).

const isBlank = (v) => v === null || v === undefined || v === '';

/** Numeric coercion that treats junk as zero. Matches the reports' long-standing `num`. */
export function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * How much of the original tread is left, 0–100, or null when the record cannot
 * say (no current depth, no initial depth, or an initial depth of zero).
 */
export function remainingTreadPct(tyre) {
  const curRaw = tyre?.tyreTreadDepth;
  const initRaw = tyre?.initialTyreTreadDepth;
  const minRaw = tyre?.minimumTreadForRetread;

  if (isBlank(curRaw) || isBlank(initRaw)) return null;

  const cur = Number(curRaw);
  const init = Number(initRaw);

  if (
    !Number.isFinite(cur) ||
    !Number.isFinite(init) ||
    init <= 0
  ) {
    return null;
  }

  // New calculation:
  // usable tread is the tread between "new" and the retread limit.
  if (!isBlank(minRaw)) {
    const min = Number(minRaw);

    if (Number.isFinite(min) && init > min) {
      const usableTread = init - min;
      const usableRemaining = cur - min;

      return Math.max(
        0,
        Math.min(100, (usableRemaining / usableTread) * 100)
      );
    }
  }

  // Legacy tyres may not have minimumTreadForRetread yet.
  // Keep their old percentage rather than showing no value.
  return Math.max(0, Math.min(100, (cur / init) * 100));
}

/**
 * Share of the tyre's distance run under-inflated, 0–100, or null when it has no
 * distance to divide by. A tyre that has never rolled is not "0% under-inflated";
 * it is unmeasured, and saying 0 would quietly park it in the healthy bucket.
 */
export function underinflatedPct(tyre) {
  const km = num(tyre?.tyreKm);
  if (!km) return null;
  return (num(tyre?.tyreKmUnderinflated) / km) * 100;
}

/**
 * Fixed 2-decimal string, or null. Pressures are reported to two places
 * throughout the subsystem; keeping the precision in the value (rather than only
 * in a display formatter) means the CSV and the screen cannot drift apart on it.
 */
export function fixed2(v) {
  if (isBlank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : null;
}

/**
 * Has PressureTrack started tracking this tyre's distance automatically?
 *
 * True once the backend holds ANY pressure-distance bucket — a genuine 0 counts;
 * all-null does not. From then on the opening distance (`tyreKm`) is maintained by
 * the backend, so a write path must OMIT tyreKm rather than post the value back:
 * resending an old opening figure on an unrelated edit could overwrite the
 * backend's running total.
 *
 * One rule, two writers: the tyre record form (locks its Opening distance field)
 * and the spreadsheet importer (drops tyreKm on update).
 */
export function hasDistanceTracking(tyre) {
  return [tyre?.tyreKmOverinflated, tyre?.tyreKmNormal, tyre?.tyreKmUnderinflated]
    .some((v) => !isBlank(v));
}
